import RAPIER from '@dimforge/rapier3d-compat';
import { clamp, moveTowards } from '../engine/mathUtils';
import type { VehicleSpec } from '../vehicles/types';

/**
 * Vorzeichen-Konstanten. Rapiers Raycast-Vehicle kennt nur Achsen-*Indizes*,
 * nicht deren Richtung; die tatsaechlichen Vorzeichen haengen an der
 * gewaehlten Achsenkonvention (+Z vorne, +Y oben, +X links) und sind hier
 * zentral gebuendelt statt ueber den Code verstreut.
 */
const AXLE_CS: RAPIER.Vector = { x: -1, y: 0, z: 0 };
const SUSPENSION_DIR_CS: RAPIER.Vector = { x: 0, y: -1, z: 0 };
const UP_AXIS_INDEX = 1;
const FORWARD_AXIS_INDEX = 2;

/**
 * Headless nachgemessen (tools/vehicle-signs.mjs): mit dieser Konvention
 * dreht ein *positiver* Rapier-Lenkwinkel nach links. Die Spiel-Eingabe ist
 * aber +1 = rechts, also einmal spiegeln.
 */
const STEER_SIGN = -1;

const FORWARD_LOCAL: RAPIER.Vector = { x: 0, y: 0, z: 1 };
const DOWN_LOCAL: RAPIER.Vector = { x: 0, y: -1, z: 0 };
const UP_LOCAL: RAPIER.Vector = { x: 0, y: 1, z: 0 };

export const WHEEL_FL = 0;
export const WHEEL_FR = 1;
export const WHEEL_RL = 2;
export const WHEEL_RR = 3;

export interface DriveInput {
  /** -1 = links, +1 = rechts (wie das Lenkrad sich anfuehlt). */
  steer: number;
  throttle: number;
  brake: number;
  handbrake: boolean;
}

export interface WheelState {
  suspensionLength: number;
  rotation: number;
  steering: number;
  inContact: boolean;
  /** 0..1, wie stark die Feder gerade zusammengedrueckt ist. */
  compression: number;
  /** Seitliche Schlupfkraft, fuer Reifenquietschen und Staub. */
  sideImpulse: number;
  forwardImpulse: number;
}

export class RaycastVehicle {
  readonly spec: VehicleSpec;
  readonly body: RAPIER.RigidBody;
  readonly collider: RAPIER.Collider;
  readonly controller: RAPIER.DynamicRayCastVehicleController;
  readonly wheels: WheelState[] = [];

  /** Geglaetteter Lenkeinschlag in Radiant. */
  private steerAngle = 0;
  private world: RAPIER.World;
  private spawnPos: RAPIER.Vector;
  private spawnYaw: number;

  private readonly tmpForce = { x: 0, y: 0, z: 0 };
  private readonly rayFilter: (collider: RAPIER.Collider) => boolean;
  /** Aus `downforceAtTopSpeed` vorberechnet: F = c * v^2. */
  private readonly downforceCoefficient: number;
  private speedLimitKmh: number | null = null;

  constructor(world: RAPIER.World, spec: VehicleSpec, position: RAPIER.Vector, yaw = 0) {
    this.world = world;
    this.spec = spec;
    this.spawnPos = { ...position };
    this.spawnYaw = yaw;

    // Abtrieb wird als Anteil des Fahrzeuggewichts bei Hoechstgeschwindigkeit
    // angegeben. So bleibt der Wert unabhaengig von Masse und Tempo lesbar
    // und kann nicht versehentlich die Federung ueberlasten.
    const topSpeedMs = spec.topSpeedKmh / 3.6;
    const gravity = Math.abs(world.gravity.y) || 9.81;
    this.downforceCoefficient =
      (spec.downforceAtTopSpeed * spec.mass * gravity) / (topSpeedMs * topSpeedMs);

    const he = spec.chassisHalfExtents;
    const bodyDesc = RAPIER.RigidBodyDesc.dynamic()
      .setTranslation(position.x, position.y, position.z)
      .setRotation(yawQuat(yaw))
      .setLinearDamping(dragForTopSpeed(spec))
      .setAngularDamping(0.9)
      .setCanSleep(false)
      // Der Kollisionsquader traegt keine Masse; Masse, Schwerpunkt und
      // Traegheit werden explizit gesetzt. Ein tiefer Schwerpunkt ist der
      // wirksamste Schutz gegen Umkippen.
      .setAdditionalMassProperties(
        spec.mass,
        { x: 0, y: spec.centerOfMassY, z: 0 },
        boxInertia(spec.mass, he, spec.inertiaFactor),
        { x: 0, y: 0, z: 0, w: 1 },
      );
    this.body = world.createRigidBody(bodyDesc);

    const colDesc = RAPIER.ColliderDesc.cuboid(he.x, he.y, he.z)
      .setTranslation(0, spec.chassisOffsetY, 0)
      .setDensity(0)
      .setFriction(0.5)
      .setRestitution(0.05);
    this.collider = world.createCollider(colDesc, this.body);

    const chassisHandle = this.body.handle;
    this.rayFilter = (c) => c.parent()?.handle !== chassisHandle;

    this.controller = world.createVehicleController(this.body);
    this.controller.indexUpAxis = UP_AXIS_INDEX;
    this.controller.setIndexForwardAxis = FORWARD_AXIS_INDEX;

    const w = spec.wheels;
    const positions: RAPIER.Vector[] = [
      { x: w.halfTrack, y: w.connectionY, z: w.frontZ },
      { x: -w.halfTrack, y: w.connectionY, z: w.frontZ },
      { x: w.halfTrack, y: w.connectionY, z: w.rearZ },
      { x: -w.halfTrack, y: w.connectionY, z: w.rearZ },
    ];
    const s = spec.suspension;
    for (let i = 0; i < positions.length; i++) {
      this.controller.addWheel(
        positions[i] as RAPIER.Vector,
        SUSPENSION_DIR_CS,
        AXLE_CS,
        s.restLength,
        w.radius,
      );
      this.controller.setWheelSuspensionStiffness(i, s.stiffness);
      this.controller.setWheelSuspensionCompression(i, s.compression);
      this.controller.setWheelSuspensionRelaxation(i, s.relaxation);
      this.controller.setWheelMaxSuspensionTravel(i, s.maxTravel);
      this.controller.setWheelMaxSuspensionForce(i, s.maxForce);
      this.controller.setWheelFrictionSlip(i, spec.frictionSlip);
      this.controller.setWheelSideFrictionStiffness(i, spec.sideFrictionStiffness);
      this.wheels.push({
        suspensionLength: s.restLength,
        rotation: 0,
        steering: 0,
        inContact: false,
        compression: 0,
        sideImpulse: 0,
        forwardImpulse: 0,
      });
    }
  }

  get wheelConnectionPoints(): RAPIER.Vector[] {
    const out: RAPIER.Vector[] = [];
    for (let i = 0; i < this.controller.numWheels(); i++) {
      out.push(this.controller.wheelChassisConnectionPointCs(i) ?? { x: 0, y: 0, z: 0 });
    }
    return out;
  }

  /**
   * Laengsgeschwindigkeit in m/s (negativ = rueckwaerts).
   *
   * Bewusst nicht `controller.currentVehicleSpeed()`: das liefert den *Betrag*
   * der gesamten Geschwindigkeit und nur das Vorzeichen aus der Fahrtrichtung.
   * Beim Fallen oder Springen wuerde der Tacho damit die Fallgeschwindigkeit
   * anzeigen und die Drehmomentkurve waere in der Luft falsch.
   */
  get forwardSpeed(): number {
    const v = this.body.linvel();
    const f = rotateVector(FORWARD_LOCAL, this.body.rotation());
    return v.x * f.x + v.y * f.y + v.z * f.z;
  }

  get speedKmh(): number {
    return Math.abs(this.forwardSpeed) * 3.6;
  }

  /** Wird einmal pro festem Physikschritt vor world.step() aufgerufen. */
  update(input: DriveInput, dt: number): void {
    const spec = this.spec;
    const speed = this.forwardSpeed;
    const absKmh = Math.abs(speed) * 3.6;

    // --- Lenkung: weich aufbauen, bei Tempo begrenzen, selbst zentrieren ---
    const speedFactor =
      1 - (1 - spec.highSpeedSteerFactor) * clamp(absKmh / spec.topSpeedKmh, 0, 1);
    const maxSteer = (spec.maxSteerDeg * Math.PI) / 180;
    const targetSteer = clamp(input.steer, -1, 1) * maxSteer * speedFactor * STEER_SIGN;
    const returning = Math.abs(input.steer) < 0.01;
    const rate = (returning ? spec.steerReturnRate : spec.steerRate) * maxSteer;
    this.steerAngle = moveTowards(this.steerAngle, targetSteer, rate * dt);

    this.controller.setWheelSteering(WHEEL_FL, this.steerAngle);
    this.controller.setWheelSteering(WHEEL_FR, this.steerAngle);

    // --- Gas, Bremse, Rueckwaerts ---
    // Der rote Knopf ist Bremse solange wir vorwaerts rollen und wird
    // darunter automatisch zum Rueckwaertsgang. Ein Kind muss nichts schalten.
    const rollingForward = speed > 0.6;
    const rollingBackward = speed < -0.6;

    let engineForce = 0;
    let brake = 0;

    if (input.throttle > 0.01) {
      if (rollingBackward) {
        brake = spec.brakeForce * input.throttle;
      } else {
        engineForce = spec.engineForce * input.throttle * this.torqueCurve(speed, false);
      }
    }

    if (input.brake > 0.01) {
      if (rollingForward) {
        brake = spec.brakeForce * input.brake;
      } else {
        engineForce = -spec.engineForce * 0.55 * input.brake * this.torqueCurve(speed, true);
      }
    }

    // Im Parkmodus bleibt das Fahrzeug kontrollierbar und kann nicht mit
    // Vollgas durch stehende Autos oder Begrenzungen schiessen.
    if (this.speedLimitKmh !== null && absKmh >= this.speedLimitKmh) {
      const acceleratingForward = engineForce > 0 && speed >= 0;
      const acceleratingBackward = engineForce < 0 && speed <= 0;
      if (acceleratingForward || acceleratingBackward) {
        engineForce = 0;
        brake = Math.max(brake, spec.brakeForce * 0.055);
      }
    }

    if (input.throttle < 0.01 && input.brake < 0.01) {
      brake = spec.brakeForce * spec.engineBrakeFactor;
    }

    const drivenWheels = this.drivenWheelMask();
    for (let i = 0; i < 4; i++) {
      this.controller.setWheelEngineForce(i, (drivenWheels[i] ?? 0) * engineForce);
      this.controller.setWheelBrake(i, brake);
    }

    // --- Handbremse: hinten blockieren und Seitengrip senken -> Drift ---
    if (input.handbrake) {
      this.controller.setWheelBrake(WHEEL_RL, spec.handbrakeForce);
      this.controller.setWheelBrake(WHEEL_RR, spec.handbrakeForce);
      this.controller.setWheelEngineForce(WHEEL_RL, 0);
      this.controller.setWheelEngineForce(WHEEL_RR, 0);
      const grip = spec.sideFrictionStiffness * spec.handbrakeGripFactor;
      this.controller.setWheelSideFrictionStiffness(WHEEL_RL, grip);
      this.controller.setWheelSideFrictionStiffness(WHEEL_RR, grip);
    } else {
      this.controller.setWheelSideFrictionStiffness(WHEEL_RL, spec.sideFrictionStiffness);
      this.controller.setWheelSideFrictionStiffness(WHEEL_RR, spec.sideFrictionStiffness);
    }

    this.applyDownforce(speed, dt);
    this.controller.updateVehicle(dt, undefined, undefined, this.rayFilter);
    this.readWheelStates();
  }

  /**
   * Drehmomentkurve: viel Schub von unten, spaet abfallend zur
   * Hoechstgeschwindigkeit. Ohne das faehrt sich jedes Auto gleich.
   */
  private torqueCurve(speed: number, reverse: boolean): number {
    const topMs = ((reverse ? this.spec.reverseSpeedKmh : this.spec.topSpeedKmh) / 3.6) || 1;
    const t = clamp(Math.abs(speed) / topMs, 0, 1);
    // Anfahrbonus unter 25 % der Endgeschwindigkeit.
    const launch = 1 + 0.45 * (1 - clamp(t / 0.25, 0, 1));
    return Math.max(0, 1 - t * t * t) * launch;
  }

  private drivenWheelMask(): readonly number[] {
    return drivenWheelMask(this.spec.drive);
  }

  /**
   * Abtrieb entlang der Fahrzeug-Unterseite: haelt das Auto satt am Boden.
   *
   * Bewusst als Impuls (Kraft * dt) und nicht ueber `addForce`: eine mit
   * `addForce` gesetzte Kraft wirkt in Rapier so lange weiter, bis
   * `resetForces` gerufen wird - jeden Schritt neu addiert waere das eine
   * unbegrenzt wachsende Kraft, die das Fahrzeug durch die Federung in den
   * Boden druecken wuerde.
   */
  private applyDownforce(speed: number, dt: number): void {
    const magnitude = this.downforceCoefficient * speed * speed;
    if (magnitude < 1) return;
    const down = rotateVector(DOWN_LOCAL, this.body.rotation());
    this.tmpForce.x = down.x * magnitude * dt;
    this.tmpForce.y = down.y * magnitude * dt;
    this.tmpForce.z = down.z * magnitude * dt;
    this.body.applyImpulse(this.tmpForce, true);
  }

  private readWheelStates(): void {
    const rest = this.spec.suspension.restLength;
    const travel = this.spec.suspension.maxTravel;
    for (let i = 0; i < this.wheels.length; i++) {
      const w = this.wheels[i];
      if (!w) continue;
      w.suspensionLength = this.controller.wheelSuspensionLength(i) ?? rest;
      w.rotation = this.controller.wheelRotation(i) ?? 0;
      w.steering = this.controller.wheelSteering(i) ?? 0;
      w.inContact = this.controller.wheelIsInContact(i);
      w.compression = clamp((rest - w.suspensionLength) / Math.max(travel, 0.001), -1, 1);
      w.sideImpulse = this.controller.wheelSideImpulse(i) ?? 0;
      w.forwardImpulse = this.controller.wheelForwardImpulse(i) ?? 0;
    }
  }

  /** Setzt das Fahrzeug aufrecht an Ort und Stelle zurueck. */
  resetUpright(): void {
    const p = this.body.translation();
    const yaw = currentYaw(this.body.rotation());
    this.body.setTranslation({ x: p.x, y: p.y + 1.0, z: p.z }, true);
    this.body.setRotation(yawQuat(yaw), true);
    this.body.setLinvel({ x: 0, y: 0, z: 0 }, true);
    this.body.setAngvel({ x: 0, y: 0, z: 0 }, true);
    this.steerAngle = 0;
  }

  /** Zurueck zum Startpunkt. */
  respawn(): void {
    this.body.setTranslation(this.spawnPos, true);
    this.body.setRotation(yawQuat(this.spawnYaw), true);
    this.body.setLinvel({ x: 0, y: 0, z: 0 }, true);
    this.body.setAngvel({ x: 0, y: 0, z: 0 }, true);
    this.steerAngle = 0;
  }

  setSpawn(position: RAPIER.Vector, yaw: number): void {
    this.spawnPos = { ...position };
    this.spawnYaw = yaw;
  }

  setSpeedLimit(limitKmh: number | null): void {
    this.speedLimitKmh = limitKmh === null ? null : Math.max(5, limitKmh);
  }

  /** Wie stark das Fahrzeug auf der Seite liegt (1 = aufrecht, -1 = auf dem Dach). */
  get uprightness(): number {
    const up = rotateVector(UP_LOCAL, this.body.rotation());
    return up.y;
  }

  dispose(): void {
    this.world.removeRigidBody(this.body);
  }
}

function drivenWheelMask(drive: VehicleSpec['drive']): readonly number[] {
  switch (drive) {
    case 'fwd':
      return [1, 1, 0, 0];
    case 'rwd':
      return [0, 0, 1, 1];
    default:
      return [0.5, 0.5, 0.5, 0.5];
  }
}

/**
 * Lineare Daempfung als Ersatz fuer Luft- und Rollwiderstand.
 *
 * Statt einen Zahlenwert zu raten wird sie so bestimmt, dass sich Antriebs-
 * und Widerstandskraft bei 95 % der gewuenschten Hoechstgeschwindigkeit
 * aufheben. Damit stimmt `topSpeedKmh` in der Fahrzeugdatei tatsaechlich mit
 * dem ueberein, was das Fahrzeug erreicht.
 */
function dragForTopSpeed(spec: VehicleSpec): number {
  const mask = drivenWheelMask(spec.drive);
  const drivenSum = mask.reduce<number>((a, b) => a + b, 0);
  const totalForce = spec.engineForce * drivenSum;
  const vTop = spec.topSpeedKmh / 3.6;
  const t = 0.95;
  const remaining = 1 - t * t * t;
  const damping = (totalForce * remaining) / (spec.mass * t * vTop);
  return clamp(damping, 0.008, 0.25);
}

// --- kleine Mathe-Helfer (Rapier-Vektoren, kein Three.js in der Physik) ---

function boxInertia(
  mass: number,
  he: { x: number; y: number; z: number },
  factor: number,
): RAPIER.Vector {
  const k = (mass / 3) * factor;
  return {
    x: k * (he.y * he.y + he.z * he.z),
    y: k * (he.x * he.x + he.z * he.z),
    z: k * (he.x * he.x + he.y * he.y),
  };
}

function yawQuat(yaw: number): RAPIER.Rotation {
  return { x: 0, y: Math.sin(yaw / 2), z: 0, w: Math.cos(yaw / 2) };
}

function currentYaw(q: RAPIER.Rotation): number {
  // Yaw aus der Vorwaertsachse ableiten - stabil auch bei starker Neigung.
  const f = rotateVector(FORWARD_LOCAL, q);
  return Math.atan2(f.x, f.z);
}

function rotateVector(v: RAPIER.Vector, q: RAPIER.Rotation): RAPIER.Vector {
  const { x, y, z } = v;
  const { x: qx, y: qy, z: qz, w: qw } = q;
  const ix = qw * x + qy * z - qz * y;
  const iy = qw * y + qz * x - qx * z;
  const iz = qw * z + qx * y - qy * x;
  const iw = -qx * x - qy * y - qz * z;
  return {
    x: ix * qw + iw * -qx + iy * -qz - iz * -qy,
    y: iy * qw + iw * -qy + iz * -qx - ix * -qz,
    z: iz * qw + iw * -qz + ix * -qy - iy * -qx,
  };
}
